import { Link, useLocation } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { holds } from '../../../layouts/ConsoleShell';
import { SOURCE_LABEL, type LeadRow } from '../routes/Leads';
import { formatDate } from '../../../lib/format';

/**
 * The enquiry a company or a venue came from.
 *
 * Converting a lead registers the organisation, and until now the trail went
 * one way only: the lead pointed at the company, and the company page said
 * nothing about the lead. Whoever picked up the registration for approval
 * could not see the calls, notes and promises that led to it. This line is
 * that link back, shown only to people who can read the inbox and only when
 * there is an enquiry to point at.
 */
export function LeadOrigin({ tenantId, partnerId }: { tenantId?: string; partnerId?: string }) {
  const { permissions } = useAuth();
  const canRead = holds(permissions, 'lead.read');
  const base = useLocation().pathname.startsWith('/ops') ? '/ops/leads' : '/admin/leads';
  const filter = tenantId ? `convertedTenantId=${tenantId}` : `convertedPartnerId=${partnerId}`;

  const lead = useQuery<{ items: LeadRow[] }>(
    () => (canRead && (tenantId || partnerId)
      ? api.get(`/leads?limit=1&${filter}`)
      : Promise.resolve({ items: [] })),
    [canRead, filter],
  );
  const l = lead.data?.items[0];
  if (!l) return null;

  return (
    <p className="flex flex-wrap items-center gap-1.5 text-caption text-neutral-600">
      <Inbox className="h-3.5 w-3.5 text-neutral-500" aria-hidden />
      Came in as an enquiry
      {l.source ? ` via ${(SOURCE_LABEL[l.source] ?? l.source.replace(/_/g, ' ')).toLowerCase()}` : ''} on{' '}
      {formatDate(l.createdAt)}
      {l.assignedTo ? `, worked by ${l.assignedTo.fullName}` : ''}.
      <Link to={`${base}/${l.id}`} className="inline-flex min-h-6 items-center tap-area font-medium text-primary-600 hover:text-primary-700">
        Open the lead
      </Link>
    </p>
  );
}
